import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { FileCheck, Plus, Search, Euro, Calendar, CheckCircle, Clock, XCircle, AlertCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPES_AIDE = {
  cee: { label: "CEE", color: "bg-blue-100 text-blue-800" },
  maprimerenov: { label: "MaPrimeRénov", color: "bg-green-100 text-green-800" },
  anah: { label: "ANAH", color: "bg-purple-100 text-purple-800" },
  tva_reduite: { label: "TVA Réduite", color: "bg-orange-100 text-orange-800" },
};

const STATUTS_DOSSIER = {
  brouillon: { label: "Brouillon", icon: Clock, color: "bg-gray-100 text-gray-800" },
  en_preparation: { label: "En préparation", icon: Clock, color: "bg-blue-100 text-blue-800" },
  depose: { label: "Déposé", icon: FileCheck, color: "bg-purple-100 text-purple-800" },
  en_instruction: { label: "En instruction", icon: Clock, color: "bg-orange-100 text-orange-800" },
  accepte: { label: "Accepté", icon: CheckCircle, color: "bg-green-100 text-green-800" },
  refuse: { label: "Refusé", icon: XCircle, color: "bg-red-100 text-red-800" },
  verse: { label: "Versé", icon: CheckCircle, color: "bg-emerald-100 text-emerald-800" },
};

const DossiersAidesPage = ({ user }) => {
  const [dossiers, setDossiers] = useState([]);
  const [projets, setProjets] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatut, setFilterStatut] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [showDialog, setShowDialog] = useState(false);
  const [editingDossier, setEditingDossier] = useState(null);

  const emptyDossier = {
    projet_id: "",
    client_id: "",
    type_aide: "cee",
    statut: "brouillon",
    montant_demande: 0,
    montant_accorde: 0,
    date_depot: "",
    date_decision: "",
    date_versement: "",
    numero_dossier: "",
    documents_fournis: [],
    notes: "",
  };

  const [formData, setFormData] = useState(emptyDossier);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const token = localStorage.getItem("token");
      const [dossRes, projRes, cliRes] = await Promise.all([
        axios.get(`${API}/dossiers-aides`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/projets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setDossiers(dossRes.data);
      setProjets(projRes.data);
      setClients(cliRes.data);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem("token");
      if (editingDossier) {
        await axios.put(`${API}/dossiers-aides/${editingDossier.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Dossier mis à jour !");
      } else {
        await axios.post(`${API}/dossiers-aides`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Dossier créé !");
      }
      setShowDialog(false);
      setEditingDossier(null);
      fetchAll();
      setFormData(emptyDossier);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleEdit = (dossier) => {
    setFormData({
      ...dossier,
      date_depot: dossier.date_depot ? dossier.date_depot.split('T')[0] : "",
      date_decision: dossier.date_decision ? dossier.date_decision.split('T')[0] : "",
      date_versement: dossier.date_versement ? dossier.date_versement.split('T')[0] : "",
    });
    setEditingDossier(dossier);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce dossier ?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/dossiers-aides/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Dossier supprimé");
      fetchAll();
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const filteredDossiers = dossiers.filter((d) => {
    const matchSearch = 
      d.numero_dossier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getClientName(d.client_id).toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatut = filterStatut === "all" || d.statut === filterStatut;
    const matchType = filterType === "all" || d.type_aide === filterType;
    return matchSearch && matchStatut && matchType;
  });

  const stats = {
    total: dossiers.length,
    en_cours: dossiers.filter(d => ["en_preparation", "depose", "en_instruction"].includes(d.statut)).length,
    acceptes: dossiers.filter(d => d.statut === "accepte").length,
    verses: dossiers.filter(d => d.statut === "verse").length,
    montant_total_demande: dossiers.reduce((sum, d) => sum + (d.montant_demande || 0), 0),
    montant_total_accorde: dossiers.reduce((sum, d) => sum + (d.montant_accorde || 0), 0),
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.prenom || ""} ${client.nom || ""}`.trim() : "Client inconnu";
  };

  const getProjetTitre = (projetId) => {
    const projet = projets.find(p => p.id === projetId);
    return projet?.titre || "Projet inconnu";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
            <FileCheck className="w-8 h-8 text-red-600" />
            Dossiers d'Aides
          </h1>
          <p className="text-slate-600 mt-1">Gérez les demandes CEE, MaPrimeRénov et autres aides</p>
        </div>
        <Button onClick={() => { setEditingDossier(null); setFormData(emptyDossier); setShowDialog(true); }} className="bg-red-600 hover:bg-red-700">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau dossier
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: "Total dossiers", value: stats.total, color: "text-slate-800" },
          { label: "En cours", value: stats.en_cours, color: "text-blue-600" },
          { label: "Acceptés", value: stats.acceptes, color: "text-green-600" },
          { label: "Versés", value: stats.verses, color: "text-emerald-600" },
        ].map((stat, idx) => (
          <Card key={idx}>
            <CardContent className="pt-6">
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
              <p className="text-sm text-slate-600">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Montants */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Euro className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-slate-600">Montant Total Demandé</p>
                <p className="text-2xl font-bold text-blue-700">
                  {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(stats.montant_total_demande)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-sm text-slate-600">Montant Total Accordé</p>
                <p className="text-2xl font-bold text-green-700">
                  {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(stats.montant_total_accorde)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[300px]">
              <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par n° dossier ou client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={filterStatut}
              onChange={(e) => setFilterStatut(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="all">Tous les statuts</option>
              {Object.entries(STATUTS_DOSSIER).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="all">Tous les types</option>
              {Object.entries(TYPES_AIDE).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <div className="grid grid-cols-1 gap-4">
        {filteredDossiers.map((dossier) => {
          const TypeConfig = TYPES_AIDE[dossier.type_aide] || TYPES_AIDE.cee;
          const StatutConfig = STATUTS_DOSSIER[dossier.statut] || STATUTS_DOSSIER.brouillon;
          const StatutIcon = StatutConfig.icon;
          
          return (
            <Card key={dossier.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <FileCheck className="w-5 h-5 text-red-600" />
                      <h3 className="text-lg font-semibold">
                        {dossier.numero_dossier || "Sans n° dossier"}
                      </h3>
                      <Badge className={TypeConfig.color}>{TypeConfig.label}</Badge>
                      <Badge className={StatutConfig.color}>
                        <StatutIcon className="w-3 h-3 mr-1" />
                        {StatutConfig.label}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Client</p>
                        <p className="font-medium">{getClientName(dossier.client_id)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Projet</p>
                        <p className="font-medium">{getProjetTitre(dossier.projet_id)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Montant demandé</p>
                        <p className="font-bold text-blue-600">
                          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(dossier.montant_demande || 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500">Montant accordé</p>
                        <p className="font-bold text-green-600">
                          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(dossier.montant_accorde || 0)}
                        </p>
                      </div>
                    </div>

                    {dossier.notes && (
                      <p className="mt-3 text-sm text-slate-600 bg-slate-50 p-2 rounded">{dossier.notes}</p>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(dossier)}>
                      Modifier
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(dossier.id)}>
                      Supprimer
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {filteredDossiers.length === 0 && (
          <Card>
            <CardContent className="pt-6 text-center text-slate-500">
              <FileCheck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucun dossier trouvé</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDossier ? "Modifier le dossier" : "Créer un nouveau dossier"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type d'aide *</Label>
                <select
                  value={formData.type_aide}
                  onChange={(e) => setFormData({...formData, type_aide: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  {Object.entries(TYPES_AIDE).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Statut *</Label>
                <select
                  value={formData.statut}
                  onChange={(e) => setFormData({...formData, statut: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  {Object.entries(STATUTS_DOSSIER).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>Projet *</Label>
              <select
                value={formData.projet_id}
                onChange={(e) => {
                  const projet = projets.find(p => p.id === e.target.value);
                  setFormData({...formData, projet_id: e.target.value, client_id: projet?.client_id || ""});
                }}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="">Sélectionner un projet</option>
                {projets.map(p => (
                  <option key={p.id} value={p.id}>{p.titre} - {getClientName(p.client_id)}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>N° Dossier</Label>
              <Input
                value={formData.numero_dossier}
                onChange={(e) => setFormData({...formData, numero_dossier: e.target.value})}
                placeholder="Ex: CEE-2024-001"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Montant demandé (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.montant_demande}
                  onChange={(e) => setFormData({...formData, montant_demande: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Montant accordé (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.montant_accorde}
                  onChange={(e) => setFormData({...formData, montant_accorde: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Date de dépôt</Label>
                <Input
                  type="date"
                  value={formData.date_depot}
                  onChange={(e) => setFormData({...formData, date_depot: e.target.value})}
                />
              </div>
              <div>
                <Label>Date de décision</Label>
                <Input
                  type="date"
                  value={formData.date_decision}
                  onChange={(e) => setFormData({...formData, date_decision: e.target.value})}
                />
              </div>
              <div>
                <Label>Date de versement</Label>
                <Input
                  type="date"
                  value={formData.date_versement}
                  onChange={(e) => setFormData({...formData, date_versement: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Notes / Commentaires</Label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
                rows={3}
                placeholder="Informations complémentaires, documents manquants, etc."
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleSubmit}
                className="bg-red-600 hover:bg-red-700"
                disabled={!formData.projet_id}
              >
                {editingDossier ? "Mettre à jour" : "Créer le dossier"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DossiersAidesPage;
